// Maps a PATHOLOGY extraction result onto the blank parameter rows of the
// New Report form (Pre Chemotherapy Vitals -> +Report).
//
// The two sides name the same test differently: the AI answers with names from
// its own target list ("Haemoglobin", "Serum Uric Acid"), while our rows come
// from `reportParamMst.reportParamName`. Nothing guarantees they agree, so the
// match is normalised rather than exact — and a miss is expected and harmless,
// it simply leaves the field for the clinician.
//
// Source shape: docs/report-scanner/api-clinical-extraction.md §4.5, which is
// the same schema as docs/api_specification.md §3.2 (the backend passes the AI
// payload through unchanged).

// Spelling and abbreviation pairs that are the same test to a clinician but
// different strings to a matcher. Applied AFTER punctuation is stripped, so
// both sides are already lowercase alphanumerics.
const SYNONYMS = [
  [/\bhaem/g, "hem"], // haemoglobin -> hemoglobin
  [/leucocyte/g, "leukocyte"],
  [/\bsgpt\b/g, "alt"],
  [/\bsgot\b/g, "ast"],
  [/\btlc\b/g, "totalleukocytecount"],
  [/\bdlc\b/g, "differentialleukocytecount"],
  [/\brbc\b/g, "redbloodcell"],
  [/\bwbc\b/g, "whitebloodcell"],
];

// Qualifiers that describe the SPECIMEN, not the test. "Serum Uric Acid" and
// "Uric Acid" are the same row; dropping these is what makes most matches land.
const SPECIMEN_PREFIXES = [
  "serum",
  "plasma",
  "blood",
  "wholeblood",
  "urine",
  "total",
];

// Reduces a parameter name to a comparable key: case, punctuation, spacing and
// specimen qualifiers all removed.
export function normalizeParamName(name) {
  if (name === null || name === undefined) return "";

  let key = String(name).toLowerCase().trim();

  // Strip anything that is not a letter or digit. This also collapses
  // "count (total)" and "count, total" onto the same key.
  key = key.replace(/[^a-z0-9]/g, "");

  SYNONYMS.forEach(([pattern, replacement]) => {
    key = key.replace(pattern, replacement);
  });

  // Only ever strip a prefix if something is left behind — "Total" on its own
  // is not a specimen qualifier, it is the whole name.
  SPECIMEN_PREFIXES.forEach((prefix) => {
    if (key.startsWith(prefix) && key.length > prefix.length) {
      key = key.slice(prefix.length);
    }
  });

  return key;
}

// Units are written inconsistently on both sides — "g/dL" vs "g/dl",
// "10^3/uL" vs "10³/µL". Compare on a reduced form rather than literally.
export function normalizeUnit(unit) {
  if (unit === null || unit === undefined) return "";
  return String(unit)
    .toLowerCase()
    .replace(/µ/g, "u")
    .replace(/³/g, "3")
    .replace(/\^/g, "")
    .replace(/[^a-z0-9/%]/g, "");
}

// Which of the AI's two values belongs in a row expecting `rowUnit`?
//
// Returns null rather than guessing. Writing a number in the wrong unit into a
// clinical field is worse than leaving it blank: the value looks authoritative,
// the unit column beside it says something else, and nothing flags the mismatch.
export function pickValueForRow(param, rowUnit) {
  if (!param) return null;

  const rowKey = normalizeUnit(rowUnit);
  const rawValue = param.value === null || param.value === undefined ? "" : String(param.value).trim();
  const convertedValue =
    param.converted_value === null || param.converted_value === undefined
      ? ""
      : String(param.converted_value).trim();

  // No unit expectation on our side — nothing to contradict, so take the value
  // as the report printed it.
  if (rowKey === "") {
    return rawValue || convertedValue || null;
  }

  if (convertedValue && normalizeUnit(param.target_unit) === rowKey) {
    return convertedValue;
  }

  if (rawValue && normalizeUnit(param.unit) === rowKey) {
    return rawValue;
  }

  // The AI reported this parameter in a unit we do not expect and could not
  // convert to ours. Leave it to the clinician.
  return null;
}

// Applies a pathology `result` to the blank rows built by
// NewReport's blankRportByIdApiCall.
//
// `rows` are objects carrying at least { id, testName, unit, result }.
// Returns NEW row objects — the caller's array is not mutated — plus what was
// filled and what could not be placed, for the notice above the table.
//
// Only `extracted_parameters` is considered. `extra_parameters` holds names
// copied verbatim out of the report rather than normalised, so matching them
// against our master would be guesswork on clinical values.
export function mapPathologyResultToRows(result, rows) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const empty = {
    rows: safeRows,
    filledCount: 0,
    filledRowIds: [],
    unmatched: [],
    unitMismatched: [],
  };

  if (!result || !Array.isArray(result.extracted_parameters)) return empty;

  const parameters = result.extracted_parameters;
  if (parameters.length === 0) return empty;

  // Index the AI's answers by normalised name. First occurrence wins: a report
  // repeating a parameter is far more likely to be a duplicate row than two
  // genuinely different results.
  const byName = new Map();
  parameters.forEach((param) => {
    const key = normalizeParamName(param && param.name);
    if (key && !byName.has(key)) byName.set(key, param);
  });

  const filledRowIds = [];
  const unitMismatched = [];
  const matchedKeys = new Set();

  const nextRows = safeRows.map((row) => {
    const key = normalizeParamName(row && row.testName);
    const param = key ? byName.get(key) : null;
    if (!param) return row;

    matchedKeys.add(key);

    // Never overwrite something the clinician has already entered.
    const existing = row.result === null || row.result === undefined ? "" : String(row.result).trim();
    if (existing !== "") return row;

    const value = pickValueForRow(param, row.unit);
    if (value === null || value === "") {
      // Matched by name but not usable in this row's unit — worth reporting
      // separately from "the report never mentioned this".
      unitMismatched.push(row.testName);
      return row;
    }

    filledRowIds.push(row.id);
    return {
      ...row,
      result: value,
      // Kept for the tooltip: the exact phrase the number came from is the
      // clinician's audit trail back to the source document.
      extractionRawText: param.raw_text || null,
      extractionFilled: true,
    };
  });

  // Parameters the report produced that no row on this form could take. Useful
  // to show, because it usually means the wrong report type was selected.
  const unmatched = [];
  byName.forEach((param, key) => {
    if (!matchedKeys.has(key)) unmatched.push(param.name);
  });

  return {
    rows: nextRows,
    filledCount: filledRowIds.length,
    filledRowIds,
    unmatched,
    unitMismatched,
  };
}
